import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Row, Col, ButtonGroup, Button } from 'reactstrap';
import TableContainerReactTable from '../../../../Components/Common/TableContainerReactTable';
import { useDeleteProductBundleMutation, useGetProductBundleQuery, useInsertProductBundleMutation, useLazyAuditProductBundleQuery, useUpdateProductBundleMutation } from '../../redux/productApiSlice';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { ParentProductData, ProductDetailsData } from 'features/products/types/types';
import LoadingIndicator from 'Components/Loading/LoadingIndicator';
import { ProductBundleItem } from 'features/products/types/ProductBundleTypes';
import type { RowSelectionState } from '@tanstack/react-table';
import { toast } from 'react-toastify';
import DeleteModal from 'Components/Common/DeleteModal';
import { useConfirmation } from 'hooks/useConfirmation';
import ParentProductModal from '../modals/ParentProductModal';

interface BundleData {
  productName: string;
  quantity: number;
  inWH: number;
  available: number;
}

interface BundleTabProps {
  isActive?: boolean;
}

const BundleTab: React.FC<BundleTabProps> = ({ isActive = false }) => {

  const selectedProduct = useSelector((state: RootState) => state.products.selectedProduct) as ProductDetailsData | null;
  const auth = useSelector((state: RootState) => state.auth);
  const productRowId = selectedProduct?.rowId || 0;

  // delete confirmation
  const { confirmation, showConfirmation, hideConfirmation, handleConfirm } = useConfirmation();


  //local state
  const [selectedBundleItem, setSelectedBundleItem] = useState<ProductBundleItem | null>(null);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [editedQuantities, setEditedQuantities] = useState<Record<number, string>>({});
  const editedQuantitiesRef = useRef<Record<number, string>>({});
  const [showPickProductModal, setShowPickProductModal] = useState<boolean>(false);

  const {
    data: productBundle,
    isLoading: isProductBundleLoading,
    isFetching: isFetchingProductBundle,
    error: productBundleError,
    refetch: refetchProductBundle
  } = useGetProductBundleQuery(
    { productRowId: productRowId },
    { skip: !isActive || !selectedProduct?.rowId }
  );

  // update bundle api
  const [updateProductBundle, { isLoading: isUpdatingProductBundle, isSuccess: isUpdatedProductBundle, isError: isErrorUpdatingProductBundle }] = useUpdateProductBundleMutation();

  // Audit product bundle api
  const [getAuditProductBundle] = useLazyAuditProductBundleQuery();
  const isRefreshing = isFetchingProductBundle && !isProductBundleLoading;

  // Delete product bundle api
  const [deleteProductBundle, { isLoading: isDeletingProductBundle, isSuccess: isDeletedProductBundle, isError: isErrorDeletingProductBundle }] = useDeleteProductBundleMutation();


  // Insert product bundle api
  const [insertProductBundle, { isLoading: isInsertingProductBundle, isSuccess: isInsertedProductBundle, isError: isErrorInsertingProductBundle }] = useInsertProductBundleMutation();

  // initialize editable quantities when data loads/refetch
  useEffect(() => {
    if (!productBundle?.data) return;
    const next: Record<number, string> = {};
    productBundle.data.forEach((item: ProductBundleItem) => {
      next[item.rowId] = (item.quantity ?? '').toString();
    });
    setEditedQuantities(next);
    editedQuantitiesRef.current = next;

    // keep selection and selected item in sync with refreshed data
    if (selectedBundleItem) {
      const updated = productBundle.data.find((i: ProductBundleItem) => i.rowId === selectedBundleItem.rowId);
      if (updated) {
        setSelectedBundleItem(updated);
        const idStr = (updated.rowId ?? updated.parentProductId ?? updated.childProductId)?.toString();
        if (idStr) {
          setRowSelection({ [idStr]: true });
        }
      } else {
        setSelectedBundleItem(null);
        setRowSelection({});
      }
    }
  }, [productBundle]);

  // keep ref in sync to avoid re-creating columns on each keystroke
  useEffect(() => {
    editedQuantitiesRef.current = editedQuantities;
  }, [editedQuantities]);

  const handleRefresh = () => {
    refetchProductBundle();
  };
  console.log(productBundle);
  const handleNew = () => {
    console.log('Creating new bundle...');
  };

  const handleSave = async () => {
    console.log('Saving bundle...');
    if (!selectedBundleItem) {
      toast.error('No bundle item selected');
      return;
    }

    const auditResponse = await getAuditProductBundle({ productRowId: productRowId, productBundleRowId: selectedBundleItem.rowId }).unwrap();

    console.log('auditResponse', auditResponse);

    if (auditResponse && auditResponse.data.updated !== selectedBundleItem.updated) {
      toast.error('This bundle item has been modified by another user since you loaded it. Please try again.');
      refetchProductBundle();
      return;
    } else {
      const userId = Number(auth?.userId) || 0;
      const qtyStr = editedQuantities[selectedBundleItem.rowId];
      if (qtyStr === undefined || qtyStr === null || qtyStr === '') {
        toast.error('Quantity is required');
        return;
      }
      const qty = parseFloat(qtyStr);
      if (!Number.isFinite(qty)) {
        toast.error('Quantity must be a valid number');
        return;
      }
      try {
        await updateProductBundle({
          productRowId: productRowId,
          body: {
            rowId: selectedBundleItem.rowId,
            userId: userId,
            quantity: qty,
            type: selectedBundleItem.type || 'bundle',
            ecomOrder: Number(selectedBundleItem.ecomOrder) || 0,
            updated: new Date().toISOString(),
          },
        }).unwrap();
        toast.success('Bundle updated successfully');
        refetchProductBundle();
      } catch (error: any) {
        toast.error(error?.data?.message || 'Failed to update bundle');
      }
    }
  };

  const resetEditedQuantities = () => {
    if (!productBundle?.data) return;
    const next: Record<number, string> = {};
    productBundle.data.forEach((item: ProductBundleItem) => {
      next[item.rowId] = (item.quantity ?? '').toString();
    });
    setEditedQuantities(next);
    editedQuantitiesRef.current = next;
  };

  const handleCancel = () => {
    if (!productBundle?.data || productBundle.data.length === 0) {
      return;
    }

    // Detect unsaved changes by comparing current edited values to server values
    const hasChanges = productBundle.data.some((item: ProductBundleItem) => {
      const original = (item.quantity ?? '').toString();
      const current = editedQuantitiesRef.current[item.rowId] ?? '';
      return current !== original;
    });

    if (!hasChanges) {
      toast.info('No changes to cancel');
      return;
    }

    showConfirmation({
      title: 'Discard changes?',
      message: 'You have unsaved changes. This will revert all quantities to the last saved values.',
      recordName: 'bundle edits',
      deleteButtonText: 'Yes, Discard Changes',
      cancelButtonText: 'Keep Editing',
      variant: 'cancel',
      onConfirm: async () => {
        resetEditedQuantities();
        toast.success('Changes discarded');
      }
    });
  };

  const handleDelete = () => {
    if (!selectedBundleItem) {
      toast.error('No bundle item selected');
      return;
    }

    showConfirmation({
      title: 'Delete Bundle',
      message: 'Are you sure you want to delete this bundle item? This action cannot be undone.',
      recordName: 'bundle item',
      recordId: selectedBundleItem.rowId?.toString(),
      onConfirm: performDeleteBundle
    });
    console.log('Deleting bundle...');
  };

  const performDeleteBundle = async () => {
    try {
      await deleteProductBundle({ productRowId: productRowId, productBundleRowId: selectedBundleItem?.rowId || 0 }).unwrap();
      toast.success('Bundle deleted successfully');
      refetchProductBundle();
      setRowSelection({});
      setSelectedBundleItem(null);
      setEditedQuantities({});
      editedQuantitiesRef.current = {};
      hideConfirmation();
    } catch (error) {
      toast.error('Failed to delete bundle');
      throw error; // Re-throw to keep modal open on error
    }
  };

  const handleConfigurator = () => {
    console.log('Opening configurator...');
  };

  const handleViewProduct = () => {
    console.log('Viewing product...');
  };

  // Insert Product Bundle Modal
  const handleOpenPickProductModal = () => setShowPickProductModal(true);
  const handleClosePickProductModal = () => setShowPickProductModal(false);

  const handleInsertProductBundle = async (selectedProductBundle: ParentProductData, quantity: number) => {

    console.log('selectedProductBundle', selectedProductBundle);
    console.log('quantity', quantity);

    try {

      const body = {
        parentProductId: selectedProduct?.rowId || 0,
        childProductId: selectedProductBundle?.rowId || 0,
        quantity: quantity,
        type: 'Bundle',
        ecomOrder: 0,
        updated: selectedProductBundle?.updatedDate
      }

      await insertProductBundle({
        productRowId: productRowId,
        body: body
      }).unwrap();
      toast.success('Product bundle inserted successfully');
      refetchProductBundle();
      setShowPickProductModal(false);
      setSelectedBundleItem(null);
      setRowSelection({});
      setEditedQuantities({});
      editedQuantitiesRef.current = {};
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to insert product bundle');
    }
  };

  // Sample data (unused)
  const data: BundleData[] = [];

  // Column definitions
  const columns = useMemo(
    () => [
      {
        header: "Product Name",
        accessorKey: "productName",
        enableColumnFilter: false,
        size: 200,
      },
      {
        header: "Quantity",
        accessorKey: "quantity",
        enableColumnFilter: false,
        size: 120,
        cell: (cell: any) => {
          const row = cell.row?.original as ProductBundleItem;
          const original = cell.getValue()?.toString() || '';
          const current = editedQuantitiesRef.current[row?.rowId];
          const displayValue = current !== undefined && current !== null ? current : original;
          return (
            <div className="d-flex justify-content-start" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
              <input
                type="text"
                className="form-control form-control-sm text-center"
                style={{ maxWidth: 90 }}
                aria-label={`Quantity for ${row?.productName}`}
                value={displayValue}
                onFocus={(e) => {
                  e.currentTarget.select();
                  const id = cell.row.id as string;
                  setRowSelection({ [id]: true });
                  setSelectedBundleItem(cell.row.original as ProductBundleItem);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                onChange={(e) => {
                  const newValue = e.target.value;
                  editedQuantitiesRef.current = { ...editedQuantitiesRef.current, [row.rowId]: newValue };
                  setEditedQuantities((prev) => ({ ...prev, [row.rowId]: newValue }));
                  const id = cell.row.id as string;
                  setRowSelection({ [id]: true });
                  setSelectedBundleItem(cell.row.original as ProductBundleItem);
                }}
                disabled={isUpdatingProductBundle}
              />
            </div>
          );
        },
      },
      {
        header: "In WH",
        accessorKey: "inWH",
        enableColumnFilter: false,
        size: 100,
        cell: (cell: any) => {
          return (
            <div className="text-right">
              {cell.getValue()}
            </div>
          );
        },
      },
      {
        header: "Available",
        accessorKey: "available",
        enableColumnFilter: false,
        size: 100,
        cell: (cell: any) => {
          return (
            <div className="text-right">
              {cell.getValue()}
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="page-content p-0">
      <Row className="mb-3">
        <Col>
          <ButtonGroup className='gap-2'>
            <button
              onClick={handleOpenPickProductModal}
              className="d-inline-flex align-items-center btn btn-soft-primary btn-sm"
            >
              <i className="ri-add-line align-middle me-1"></i> New
            </button>
            <button
              onClick={handleSave}
              className="d-inline-flex align-items-center btn btn-soft-success btn-sm"
              disabled={isUpdatingProductBundle}
            >
              {isUpdatingProductBundle ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Saving
                </>
              ) : (
                <>
                  <i className="ri-save-line align-middle me-1"></i> Save
                </>
              )}
            </button>
            <button
              onClick={handleCancel}
              className="d-inline-flex align-items-center btn btn-soft-warning btn-sm"
            >
              <i className="ri-close-line align-middle me-1"></i> Cancel
            </button>
            <button
              onClick={handleDelete}
              className="d-inline-flex align-items-center btn btn-soft-danger btn-sm"
            >
              <i className="ri-delete-bin-line align-middle me-1"></i> Delete
            </button>
            <button
              onClick={handleConfigurator}
              className="d-inline-flex align-items-center btn btn-soft-info btn-sm"
            >
              <i className="ri-settings-line align-middle me-1"></i> Configurator
            </button>
            <button
              onClick={handleViewProduct}
              className="d-inline-flex align-items-center btn btn-soft-secondary btn-sm"
            >
              <i className="ri-eye-line align-middle me-1"></i> View Product
            </button>
            <button
              onClick={handleRefresh}
              className={`btn btn-outline-primary btn-sm ${isRefreshing ? 'btn-load' : ''}`}
              disabled={isRefreshing}
            >
              {isRefreshing ? (
                <span className="d-flex align-items-center">
                  <span
                    className="spinner-border flex-shrink-0"
                    role="status"
                    style={{ width: '0.875rem', height: '0.875rem', borderWidth: '1px' }}
                  >
                    <span className="visually-hidden">Loading...</span>
                  </span>
                  <span className="ms-2">
                    Refreshing...
                  </span>
                </span>
              ) : (
                <>
                  <i className="ri-refresh-line align-middle me-1"></i> Refresh
                </>
              )}
            </button>
          </ButtonGroup>
        </Col>
      </Row>

      {isProductBundleLoading ? (
        <LoadingIndicator isLoading={true} title="Loading product bundle..." height="300px" />
      ) : (
        <div className="bundle-content">
          <TableContainerReactTable
            columns={columns}
            data={productBundle?.data || []}
            divClass="table-responsive"
            tableClass="align-middle table-nowrap"
            theadClass="table-light"
            verticalLines={true}
            enableRowSelection={true}
            enableMultiRowSelection={false}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            onRowClick={(row: any) => {
              const id = row.id as string;
              setRowSelection({ [id]: true });
              setSelectedBundleItem(row.original as ProductBundleItem);
            }}
            getRowId={(row) => row.rowId?.toString() || row.productId?.toString()}
          />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmation && (
        <DeleteModal
          show={confirmation.isOpen}
          onDeleteClick={handleConfirm}
          onCloseClick={hideConfirmation}
          title={confirmation.title}
          message={confirmation.message}
          recordName={confirmation.recordName}
          recordId={confirmation.recordId}
          deleteButtonText={confirmation.deleteButtonText}
          cancelButtonText={confirmation.cancelButtonText}
          variant={confirmation.variant}
          isLoading={confirmation.isLoading}
        />
      )}

      <ParentProductModal
        isOpen={showPickProductModal}
        onClose={handleClosePickProductModal}
        onSelectQty={handleInsertProductBundle}
        selectionModal="qty"
        excludeBundleItems={productBundle?.data || []}
      />
    </div>
  );
};

export default BundleTab; 
